import React, { useEffect, useMemo } from 'react'
import { TextEditor, Text, htmlToPlainText } from '@evertel/web/ui'
import { AgreementContentView } from './AgreementContentView'

const AGREEMENT_CONTENT_MAX_LENGTH = 3000
// Default height cap for the editable agreement editor (content scrolls, toolbar stays fixed).
const AGREEMENT_EDITOR_MAX_HEIGHT = 400

interface AgreementTextEditorProps {
    value?: string
    onChange?: (html: string) => void
    editable?: boolean
    minHeight?: number
    maxHeight?: number
    footer?: React.ReactNode
    maxLength?: number
    onLengthChange?: (length: number) => void
    onReachedBottom?: () => void
}

const AgreementTextEditor: React.FC<AgreementTextEditorProps> = ({
    value,
    onChange,
    editable = true,
    minHeight = 200,
    maxHeight = editable ? AGREEMENT_EDITOR_MAX_HEIGHT : undefined,
    footer,
    maxLength,
    onLengthChange,
    onReachedBottom
}) => {
    const showCounter = editable && maxLength !== undefined
    const textLength = useMemo(
        () => (showCounter ? htmlToPlainText(value ?? '').length : 0),
        [showCounter, value]
    )
    const remaining = maxLength !== undefined ? maxLength - textLength : 0

    useEffect(() => {
        if (showCounter) onLengthChange?.(textLength)
    }, [showCounter, textLength])

    // Read-only agreement content is just stored HTML — display it directly
    // (sanitized) rather than mounting the full BlockNote editor to show it.
    if (!editable) {
        return (
            <AgreementContentView
                content={value ?? ''}
                minHeight={minHeight}
                maxHeight={maxHeight}
                footer={footer}
                onReachedBottom={onReachedBottom}
            />
        )
    }

    return (
        <>
            <TextEditor
                value={value}
                onChange={onChange}
                minHeight={minHeight}
                maxHeight={maxHeight}
                placeholder="Type the contents of your agreement here..."
                footer={footer}
                onReachedBottom={onReachedBottom}
            />
            {showCounter && (
                <Text size="smaller" className="mt-1 d-block">
                    {maxLength} characters max. You have{' '}
                    <strong className={remaining < 0 ? 'text-danger' : undefined}>{remaining}</strong>{' '}
                    characters remaining.
                </Text>
            )}
        </>
    )
}

AgreementTextEditor.displayName = 'AgreementTextEditor'
export { AgreementTextEditor, AGREEMENT_CONTENT_MAX_LENGTH }
